import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, PackageCheck, Sparkles, Package, CreditCard, MapPin, Search } from "lucide-react";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { RequestModal } from "../components/RequestModal";
import { PromoBanner } from "../components/PromoBanner";
import { NAV_CATEGORIES, HERO_IMG, iconByName, defaultCatMeta } from "../lib/categories";
import { useCurrency } from "../context/CurrencyContext";

const REGIONS = ["🇬🇧 UK · Live", "🇺🇸 USA", "🇦🇪 Dubai", "🇪🇺 Europe"];
const TRUST = [["✓", "Authentic UK products"], ["✓", "Secure payments"], ["✓", "Doorstep delivery"], ["✓", "Track every order"]];
const STORES = [
  { name: "Tesco", icon: "/store-logos/tesco.svg" },
  { name: "Sainsbury's", icon: "/store-logos/sainsburys.png" },
  { name: "ASDA", icon: "/store-logos/asda.svg" },
  { name: "Aldi", icon: "/store-logos/aldi.svg" },
  { name: "Lidl", icon: "/store-logos/lidl.svg" },
  { name: "Costco", icon: "/store-logos/costco.webp" },
  { name: "Boots", icon: "/store-logos/boots.svg" },
  { name: "Savers", icon: "/store-logos/savers.png" },
  { name: "Next", icon: "/store-logos/next.png", dark: true },
  { name: "Primark", icon: "/store-logos/primark.png" },
];

export default function Landing() {
  const [featured, setFeatured] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [cats, setCats] = useState([]);
  const [reqOpen, setReqOpen] = useState(false);
  const { format } = useCurrency();

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 8))).catch(() => {});
    api.get("/boxes").then((r) => setBoxes(r.data.slice(0, 6))).catch(() => {});
    api.get("/categories/manage").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  const displayCats = (cats.length ? cats : NAV_CATEGORIES).map((c) => {
    const d = defaultCatMeta(c.name);
    return { name: c.name, img: c.image || c.img || d?.img || "", Icon: iconByName(c.icon) || d?.Icon || Package };
  });

  return (
    <div>
      <PromoBanner placement="home_top" />
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-8 sm:pt-12">
        <div className="relative rounded-[32px] overflow-hidden grain min-h-[540px] md:min-h-[620px] flex items-center">
          <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0D530E]/85 via-[#0D530E]/45 to-transparent" />
          <div className="relative z-10 px-7 sm:px-14 py-14 max-w-2xl fade-up">
            <span className="inline-flex items-center gap-2 text-[#E7E1B1] text-xs tracking-[0.25em] uppercase font-semibold mb-6"><Sparkles size={14} /> Now shipping from the UK</span>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl font-light text-[#FBF5DD] leading-[0.95] tracking-tight">Authentic UK products delivered to your door</h1>
            <p className="text-[#FBF5DD]/85 text-lg md:text-xl mt-6 max-w-xl font-light">Shop trusted UK products. We source, ship, clear customs and deliver for you — across Ghana.</p>
            <div className="flex flex-wrap gap-3 mt-9">
              <Link to="/shop" data-testid="hero-shop-uk" className="inline-flex items-center gap-2 bg-[#306D29] hover:bg-[#0D530E] text-[#FBF5DD] font-medium px-8 py-4 rounded-full" style={{ transition: "background-color 0.2s ease" }}>Shop UK Products <ArrowRight size={18} /></Link>
              <Link to="/boxes" data-testid="hero-boxes" className="inline-flex items-center gap-2 bg-[#E7E1B1] hover:bg-[#FBF5DD] text-[#0D530E] font-medium px-8 py-4 rounded-full" style={{ transition: "background-color 0.2s ease" }}><Package size={17} /> Explore Boxes</Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-10 text-[#FBF5DD]/70 text-sm font-light">
              {REGIONS.map((r, i) => <span key={r} className={i === 0 ? "text-[#E7E1B1] font-medium" : "opacity-60"}>{r}{i > 0 && " · soon"}</span>)}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {TRUST.map(([i, l]) => (
            <div key={l} className="flex items-center gap-2.5 rounded-2xl bg-[#E7E1B1]/40 border border-[#0D530E]/5 px-5 py-4">
              <span className="w-6 h-6 rounded-full bg-[#306D29] text-[#FBF5DD] flex items-center justify-center text-xs font-bold shrink-0">{i}</span>
              <span className="text-sm font-medium text-[#0D530E]">{l}</span>
            </div>
          ))}
        </div>
      </section>

      {/* POPULAR CATEGORIES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-20">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-[#0D530E]">Shop popular categories</h2>
          <Link to="/shop" className="hidden sm:flex items-center gap-1 text-[#306D29] font-medium text-sm">All products <ArrowRight size={15} /></Link>
        </div>
        <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1 md:grid md:grid-cols-6">
          {displayCats.map((c) => (
            <Link key={c.name} to={`/category/${encodeURIComponent(c.name)}`} data-testid={`cat-${c.name}`}
              className="group shrink-0 w-40 md:w-auto rounded-2xl overflow-hidden border border-[#0D530E]/8 bg-white hover:border-[#306D29]/30 hover:shadow-[0_12px_28px_rgba(13,83,14,0.10)]" style={{ transition: "border-color 0.25s ease, box-shadow 0.25s ease" }}>
              <div className="aspect-square overflow-hidden bg-[#E7E1B1] relative">
                {c.img
                  ? <img src={c.img} alt={c.name} className="w-full h-full object-cover group-hover:scale-105" style={{ transition: "transform 0.5s ease" }} />
                  : <span className="w-full h-full flex items-center justify-center text-[#306D29]/60"><c.Icon size={42} /></span>}
                <span className="absolute top-2 left-2 w-9 h-9 rounded-full bg-[#FBF5DD]/90 text-[#306D29] flex items-center justify-center shadow-sm"><c.Icon size={17} /></span>
              </div>
              <p className="text-center font-medium text-[#0D530E] text-sm py-3">{c.name}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED BOXES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-20">
        <div className="mb-8">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#306D29] mb-2">Ready-made boxes</p>
          <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-[#0D530E]">A box for every need</h2>
        </div>
        <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1 md:grid md:grid-cols-6">
          {boxes.map((b) => {
            const { main, sub } = format(b.price_ghs, b.price_gbp);
            return (
              <Link key={b.id} to="/boxes" data-testid={`home-box-${b.id}`}
                className="group shrink-0 w-40 md:w-auto rounded-2xl overflow-hidden border border-[#0D530E]/8 bg-white flex flex-col">
                <div className="aspect-square overflow-hidden bg-[#E7E1B1]">
                  <img src={b.image} alt={b.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105" style={{ transition: "transform 0.5s ease" }} />
                </div>
                <div className="p-3 flex flex-col gap-0.5 flex-1">
                  <h3 className="font-body text-sm font-medium leading-snug line-clamp-1 text-[#0D530E]">{b.name}</h3>
                  <p className="font-display text-base font-semibold text-[#0D530E] leading-none mt-auto pt-1">{main}</p>
                  <p className="text-[11px] text-[#306D29]/70">{sub} · Buy or subscribe</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-20">
        <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-[#0D530E] mb-10">From the UK to your doorstep</h2>
        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1 md:grid md:grid-cols-3 md:gap-4 md:overflow-visible md:mx-0 md:px-0">
          {[{ icon: CreditCard, n: "01", t: "Choose", d: "Browse authentic UK products at transparent, all-inclusive prices in Cedis (GH₵)." },
            { icon: ShieldCheck, n: "02", t: "We source", d: "Our team purchases from trusted UK stores and consolidates your order." },
            { icon: Truck, n: "03", t: "We deliver", d: "We ship, clear customs and hand-deliver to your door in Ghana." }].map((s) => (
            <div key={s.n} className="shrink-0 w-[70%] sm:w-64 md:w-auto rounded-2xl bg-[#E7E1B1]/30 border border-[#0D530E]/5 p-5">
              <div className="flex items-center justify-between mb-4"><div className="w-10 h-10 rounded-full bg-[#306D29] text-[#FBF5DD] flex items-center justify-center"><s.icon size={18} /></div><span className="font-display text-3xl text-[#306D29]/30">{s.n}</span></div>
              <h3 className="font-display text-xl text-[#0D530E] mb-1.5">{s.t}</h3>
              <p className="text-sm text-[#306D29]/80 leading-relaxed font-light">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TRUSTED STORES */}
      <section className="mt-16 sm:mt-20 overflow-hidden" data-testid="trusted-stores">
        <style>{`@keyframes afg-marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}.afg-marquee-track{animation:afg-marquee 45s linear infinite}.afg-marquee-track:hover{animation-play-state:paused}`}</style>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mb-6 flex items-center gap-2 text-[#306D29]">
          <ShieldCheck size={16} />
          <p className="text-xs tracking-[0.25em] uppercase font-semibold">We shop from the UK's most trusted stores</p>
        </div>
        <div className="relative">
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 z-10 bg-gradient-to-r from-[#FBF5DD] to-transparent" />
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 z-10 bg-gradient-to-l from-[#FBF5DD] to-transparent" />
          <div className="flex gap-4 w-max afg-marquee-track px-4">
            {[...STORES, ...STORES].map((s, i) => (
              <div key={i} className={`shrink-0 h-24 min-w-[200px] px-8 rounded-2xl border border-[#0D530E]/8 shadow-sm flex items-center justify-center ${s.dark ? "bg-black" : "bg-white"}`} data-testid={`store-${s.name}`}>
                <img src={s.icon} alt={s.name} className="h-12 sm:h-14 max-w-[160px] w-auto object-contain" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRENDING PRODUCTS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-20">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-[#0D530E]">Popular this week</h2>
          <Link to="/shop" className="flex items-center gap-1 text-[#306D29] font-medium text-sm">View all <ArrowRight size={15} /></Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5">
          {featured.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* REQUEST A PRODUCT */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-20">
        <div className="rounded-[32px] bg-[#0D530E] text-[#FBF5DD] p-10 sm:p-14 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="flex items-center gap-2 text-[#E7E1B1] mb-4"><Search size={18} /><span className="text-xs tracking-[0.25em] uppercase font-semibold">Can't find it?</span></div>
            <h2 className="font-display text-4xl sm:text-5xl font-light leading-[0.95]">Need something specific?</h2>
            <p className="text-[#FBF5DD]/75 mt-5 text-lg font-light max-w-md">Tell us the exact UK product you want. We'll confirm availability and price, then source it for you. This is what makes us different.</p>
          </div>
          <div className="md:text-right">
            <button data-testid="request-product-btn" onClick={() => setReqOpen(true)} className="inline-flex items-center gap-2 bg-[#E7E1B1] hover:bg-[#FBF5DD] text-[#0D530E] font-medium px-8 py-4 rounded-full" style={{ transition: "background-color 0.2s ease" }}>
              <MapPin size={18} /> Request a product
            </button>
          </div>
        </div>
      </section>

      <RequestModal open={reqOpen} onClose={() => setReqOpen(false)} kind="product" />
    </div>
  );
}
